import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { interviewAPI, questionAPI } from '../services/api';
import { BASE_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';

function InterviewResponses() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [interview, setInterview] = useState(null);
  const [responses, setResponses] = useState([]);
  const [groupedResponses, setGroupedResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [evaluation, setEvaluation] = useState({ rating: '', notes: '' });
  const [showKeyPoints, setShowKeyPoints] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [regeneratingAI, setRegeneratingAI] = useState(false);
  const [questionFollowUps, setQuestionFollowUps] = useState({});
  const [loadingFollowUps, setLoadingFollowUps] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [savingFollowUp, setSavingFollowUp] = useState({});
  const [videoUrl, setVideoUrl] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const videoRef = React.useRef(null);

  const handleViewResponse = useCallback((response, student) => {
    setSelectedResponse(response);
    setSelectedStudent(student);
    setEvaluation({
      rating: response.rating || '',
      notes: response.notes || ''
    });
    setCurrentChapter(0);
    setVideoUrl(null); // Reset video URL when switching responses
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [interviewRes, responsesRes] = await Promise.all([
        interviewAPI.getInterview(id),
        interviewAPI.getResponses(id)
      ]);
      setInterview(interviewRes.data.interview);
      const allResponses = responsesRes.data.responses || [];
      setResponses(allResponses);
      
      // Group responses by student
      const grouped = {};
      allResponses.forEach(response => {
        const studentId = response.student?._id;
        if (studentId) {
          if (!grouped[studentId]) {
            grouped[studentId] = {
              student: response.student,
              responses: []
            };
          }
          grouped[studentId].responses.push(response);
        }
      });
      
      // Sort each student's responses by submission date (latest first)
      Object.values(grouped).forEach(group => {
        group.responses.sort((a, b) => {
          if (!a.submittedAt) return 1;
          if (!b.submittedAt) return -1;
          return new Date(b.submittedAt) - new Date(a.submittedAt);
        });
      });
      
      setGroupedResponses(grouped);

      // Pre-select response if navigationed from dashboard
      const { responseId, studentId } = location.state || {};
      if (responseId && studentId) {
        const targetResponse = allResponses.find(r => r._id === responseId);
        const targetStudent = grouped[studentId]?.student;
        if (targetResponse && targetStudent) {
          handleViewResponse(targetResponse, targetStudent);
        }
        // Clear the state to prevent re-selection on refresh
        window.history.replaceState({}, document.title);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  }, [id, location.state, handleViewResponse]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch video URL when response changes
  useEffect(() => {
    const fetchVideoUrl = async () => {
      if (selectedResponse && selectedResponse.videoRecording) {
        setLoadingVideo(true);
        try {
          const response = await interviewAPI.getResponseVideoUrl(selectedResponse._id);
          setVideoUrl(response.data.videoUrl);
        } catch (error) {
          console.error('Error fetching video URL:', error);
          // Fallback to direct URL if fetch fails
          setVideoUrl(`${BASE_URL}/${selectedResponse.videoRecording}`);
        } finally {
          setLoadingVideo(false);
        }
      }
    };

    fetchVideoUrl();
  }, [selectedResponse]);
  
  const handleChapterClick = useCallback((chapterIndex) => {
    if (videoRef.current && selectedResponse?.videoChapters) {
      const timestamp = selectedResponse.videoChapters[chapterIndex].timestamp;
      setCurrentChapter(chapterIndex);
      videoRef.current.currentTime = timestamp;
      // Use setTimeout to ensure currentTime is set before playing
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            console.log('Play was prevented:', err);
          });
        }
      }, 100);
    }
  }, [selectedResponse]);
  
  const handleVideoTimeUpdate = useCallback(() => {
    if (!videoRef.current || !selectedResponse?.videoChapters) return;
    
    const currentTime = videoRef.current.currentTime;
    const chapters = selectedResponse.videoChapters;
    
    // Find the current chapter based on video time
    let newChapter = currentChapter;
    for (let i = chapters.length - 1; i >= 0; i--) {
      if (currentTime >= chapters[i].timestamp) {
        newChapter = i;
        break;
      }
    }
    
    // Update current chapter if changed
    if (newChapter !== currentChapter) {
      setCurrentChapter(newChapter);
    }
    
    // Pause video when reaching the next chapter
    if (currentChapter < chapters.length - 1) {
      const nextChapterTimestamp = chapters[currentChapter + 1].timestamp;
      if (currentTime >= nextChapterTimestamp) {
        videoRef.current.pause();
      }
    }
  }, [selectedResponse, currentChapter]);
  
  const formatChapterTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const formatInterviewDuration = useCallback((startedAt, submittedAt) => {
    if (!startedAt || !submittedAt) return 'N/A';
    const durationMs = new Date(submittedAt) - new Date(startedAt);
    const totalMinutes = Math.floor(durationMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }, []);

  const handleSaveEvaluation = useCallback(async () => {
    try {
      await interviewAPI.evaluateResponse(selectedResponse._id, evaluation);
      alert('Evaluation saved successfully');
      fetchData();
      setSelectedResponse(null);
    } catch (error) {
      console.error('Error saving evaluation:', error);
      alert('Failed to save evaluation');
    }
  }, [selectedResponse, evaluation, fetchData]);

  const handleDeleteResponse = useCallback(async (responseId) => {
    if (!window.confirm('Are you sure you want to delete this interview response? This action cannot be undone.')) {
      return;
    }

    try {
      await interviewAPI.deleteResponse(responseId);
      alert('Interview response deleted successfully');
      fetchData();
      setSelectedResponse(null);
    } catch (error) {
      console.error('Error deleting response:', error);
      alert('Failed to delete response');
    }
  }, [fetchData]);

  const handleRegenerateAI = useCallback(async () => {
    if (!selectedResponse) return;
    
    if (!window.confirm('This will regenerate AI assessments for all questions and follow-ups. Continue?')) {
      return;
    }

    setRegeneratingAI(true);
    try {
      await interviewAPI.regenerateAI(selectedResponse._id);
      alert('AI assessment regenerated successfully! Please wait a moment for processing.');
      // Refresh the data after a short delay to allow backend processing
      setTimeout(() => {
        fetchData();
      }, 2000);
    } catch (error) {
      console.error('Error regenerating AI:', error);
      alert('Failed to regenerate AI assessment');
    } finally {
      setRegeneratingAI(false);
    }
  }, [selectedResponse, fetchData]);

  const getStatusBadge = useCallback((status) => {
    const badges = {
      not_started: { text: 'Not Started', class: 'status-not-started' },
      in_progress: { text: 'In Progress', class: 'status-in-progress' },
      submitted: { text: 'Submitted', class: 'status-submitted' },
      timed_out: { text: 'Timed Out', class: 'status-timed-out' }
    };
    return badges[status] || { text: status, class: '' };
  }, []);

  const getAnswerForQuestion = useCallback((response, questionId) => {
    if (!response || !response.responses || !questionId) return null;
    const answer = response.responses.find(r => {
      if (!r || !r.questionId) return false;
      const rqId = r.questionId._id || r.questionId;
      return rqId === questionId;
    });
    return answer;
  }, []);

  const fetchFollowUpsForQuestion = useCallback(async (questionId) => {
    if (questionFollowUps[questionId]) {
      // Already loaded, just toggle
      setExpandedQuestions(prev => ({ ...prev, [questionId]: !prev[questionId] }));
      return;
    }

    setLoadingFollowUps(prev => ({ ...prev, [questionId]: true }));
    try {
      const response = await questionAPI.getQuestionFollowUps(questionId);
      setQuestionFollowUps(prev => ({ ...prev, [questionId]: response.data.followUps || [] }));
      setExpandedQuestions(prev => ({ ...prev, [questionId]: true }));
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      alert('Failed to fetch follow-up questions');
    } finally {
      setLoadingFollowUps(prev => ({ ...prev, [questionId]: false }));
    }
  }, [questionFollowUps]);

  const handleSaveFollowUpToBank = useCallback(async (followUpId, questionId) => {
    if (!window.confirm('Save this follow-up question to the question bank?')) {
      return;
    }

    setSavingFollowUp(prev => ({ ...prev, [followUpId]: true }));
    try {
      await questionAPI.saveFollowUpToBank({ followUpId });
      alert('Follow-up question saved to question bank successfully!');
      // Refresh the follow-ups for this question
      const response = await questionAPI.getQuestionFollowUps(questionId);
      setQuestionFollowUps(prev => ({ ...prev, [questionId]: response.data.followUps || [] }));
    } catch (error) {
      console.error('Error saving follow-up:', error);
      alert(error.response?.data?.message || 'Failed to save follow-up question');
    } finally {
      setSavingFollowUp(prev => ({ ...prev, [followUpId]: false }));
    }
  }, []);

  const generateDetailedReport = useCallback(() => {
    if (!selectedResponse || !interview) return;

    const student = selectedResponse.student || selectedStudent;
    const attemptNumber = selectedStudent && groupedResponses[selectedStudent._id] 
      ? groupedResponses[selectedStudent._id].responses.findIndex(r => r._id === selectedResponse._id) + 1
      : 1;
    const totalAttempts = selectedStudent && groupedResponses[selectedStudent._id]
      ? groupedResponses[selectedStudent._id].responses.length
      : 1;

    let report = `===========================================
INTERVIEW RESPONSE DETAILED REPORT
===========================================

INTERVIEW DETAILS:
------------------
Title: ${interview.title}
Description: ${interview.description || 'N/A'}
Difficulty: ${interview.difficulty ? interview.difficulty.toUpperCase() : 'Not specified'}
Duration: ${interview.duration} minutes
AI Evaluation: ${interview.showAIEvaluation ? 'Enabled' : 'Disabled'}

STUDENT INFORMATION:
-------------------
Name: ${student?.name || 'Unknown'}
Email: ${student?.email || 'N/A'}
Attempt: ${attemptNumber} of ${totalAttempts}
Submission Date: ${selectedResponse.submittedAt ? new Date(selectedResponse.submittedAt).toLocaleString() : 'Not submitted'}
Status: ${selectedResponse.status.toUpperCase()}
Time Taken: ${formatInterviewDuration(selectedResponse.startedAt, selectedResponse.submittedAt)}

ADMIN EVALUATION:
----------------
Rating: ${selectedResponse.rating ? `${selectedResponse.rating}/10` : 'Not rated yet'}
Admin Notes: ${selectedResponse.notes || 'No notes provided'}

`;

    // Add video recording info
    if (selectedResponse.videoRecording) {
      report += `VIDEO RECORDING:
-----------------
Available: Yes
URL: ${BASE_URL}/${selectedResponse.videoRecording}

`;
    }

    // Add questions and answers
    report += `QUESTIONS & ANSWERS:
====================

`;

    interview.questions.forEach((q, idx) => {
      if (!q || !q.questionId) return;
      const answer = getAnswerForQuestion(selectedResponse, q.questionId._id || q.questionId);
      report += `QUESTION ${idx + 1}:
${'-'.repeat(80)}
${q.questionId.question || 'Question not available'}

Type: ${q.questionId.questionType || 'N/A'}
${q.questionId.difficulty ? `Difficulty: ${q.questionId.difficulty}` : ''}

`;

      if (answer) {
        // Display cleaned answer if available, otherwise show original
        if (q.questionId.questionType === 'MCQ' && q.questionId.options) {
          report += `STUDENT ANSWER:
${q.questionId.options[parseInt(answer.answer)] || answer.answer}

`;
        } else {
          const displayAnswer = answer.cleanedAnswer || answer.answer || 'No answer provided';
          const label = answer.cleanedAnswer ? 'ANSWER (AI-Cleaned)' : 'ANSWER';
          report += `${label}:
${displayAnswer}

`;
        }

        // AI Assessment
        if (answer.aiAssessment) {
          report += `AI ASSESSMENT:
Score: ${answer.aiAssessment.score}/100
Feedback: ${answer.aiAssessment.feedback}

`;
          if (answer.aiAssessment.strengths && answer.aiAssessment.strengths.length > 0) {
            report += `Strengths:
`;
            answer.aiAssessment.strengths.forEach((s, i) => {
              report += `  ${i + 1}. ${s}\n`;
            });
            report += `\n`;
          }

          if (answer.aiAssessment.improvements && answer.aiAssessment.improvements.length > 0) {
            report += `Areas for Improvement:
`;
            answer.aiAssessment.improvements.forEach((imp, i) => {
              report += `  ${i + 1}. ${imp}\n`;
            });
            report += `\n`;
          }
        }

        // Time spent
        if (answer.timeSpent) {
          report += `Time Spent: ${Math.round(answer.timeSpent / 1000)} seconds\n`;
        }

        // AI Processing Status
        if (answer.aiProcessingStatus && answer.aiProcessingStatus !== 'not_required') {
          report += `AI Processing Status: ${answer.aiProcessingStatus.toUpperCase()}\n`;
        }

        report += `\n`;

        // Key evaluation points
        if (q.questionId.interviewerPoints && q.questionId.interviewerPoints.length > 0) {
          report += `KEY EVALUATION POINTS:
`;
          q.questionId.interviewerPoints.forEach((point, i) => {
            report += `  ${i + 1}. ${point}\n`;
          });
          report += `\n`;
        }
      } else {
        report += `STUDENT ANSWER: Not answered\n\n`;
      }

      report += `\n`;
    });

    // Add Follow-up Questions section if any
    const followUpResponses = selectedResponse.responses?.filter(r => r.isFollowUp) || [];
    if (followUpResponses.length > 0) {
      report += `
AI FOLLOW-UP QUESTIONS:
=======================

`;
      followUpResponses.forEach((followUp, idx) => {
        const parentIndex = interview.questions.findIndex(q => {
          if (!q || !q.questionId) return false;
          const qId = q.questionId._id || q.questionId;
          const parentId = followUp.parentQuestionId?._id || followUp.parentQuestionId;
          return qId === parentId;
        });
        
        const displayAnswer = followUp.cleanedAnswer || followUp.answer || 'No answer provided';
        const label = followUp.cleanedAnswer ? 'ANSWER (AI-Cleaned)' : 'ANSWER';
        report += `FOLLOW-UP ${idx + 1} (Based on Q${parentIndex >= 0 ? parentIndex + 1 : '?'}):
${'-'.repeat(80)}
${followUp.followUpQuestion || 'Follow-up question'}

${label}:
${displayAnswer}

`;
        if (followUp.timeSpent) {
          report += `Time Spent: ${Math.round(followUp.timeSpent / 1000)} seconds\n`;
        }
        report += `\n`;
      });
    }

    report += `===========================================
END OF REPORT
===========================================`;

    return report;
  }, [selectedResponse, interview, selectedStudent, groupedResponses, formatInterviewDuration, getAnswerForQuestion]);

  const handleDownloadReport = useCallback(() => {
    const report = generateDetailedReport();
    if (!report) return;

    const blob = new Blob([report], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const student = selectedResponse.student || selectedStudent;
    const fileName = `Interview_${interview.title}_${student?.name || 'Student'}_${new Date().toISOString().split('T')[0]}.txt`;
    a.download = fileName.replace(/[^a-z0-9._-]/gi, '_');
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, [generateDetailedReport, selectedResponse, interview, selectedStudent]);

  const handleCopyReport = useCallback(() => {
    const report = generateDetailedReport();
    if (!report) return;

    navigator.clipboard.writeText(report).then(() => {
      alert('Report copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy report to clipboard');
    });
  }, [generateDetailedReport]);

  // Memoize students list to prevent unnecessary re-computations
  const studentsList = useMemo(() => Object.values(groupedResponses), [groupedResponses]);

  // Calculate overall performance metrics
  const performanceMetrics = useMemo(() => {
    if (!selectedResponse || !interview) return null;

    const questions = interview.questions || [];
    const responses = selectedResponse.responses || [];
    
    // Filter responses to main questions only (not follow-ups)
    const mainAnswers = responses.filter(r => !r.isFollowUp);
    const answeredCount = mainAnswers.filter(r => r.answer && r.answer.trim()).length;
    
    // Calculate average AI score from main questions
    const scoredAnswers = mainAnswers.filter(r => r.aiAssessment && r.aiAssessment.score !== undefined);
    const avgAIScore = scoredAnswers.length > 0 
      ? scoredAnswers.reduce((sum, r) => sum + r.aiAssessment.score, 0) / scoredAnswers.length 
      : null;
    
    // Follow-up questions
    const followUpAnswers = responses.filter(r => r.isFollowUp);
    const answeredFollowUps = followUpAnswers.filter(r => r.answer && r.answer.trim()).length;
    
    return {
      totalQuestions: questions.length,
      answeredCount,
      avgAIScore,
      overallScore: selectedResponse.overallFeedback?.overallScore,
      adminRating: selectedResponse.rating,
      followUpCount: followUpAnswers.length,
      answeredFollowUps,
      status: selectedResponse.status
    };
  }, [selectedResponse, interview]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>Loading responses...</div>;
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px' }}>
          <button onClick={() => navigate('/interviews')} className="btn btn-secondary" style={{ marginBottom: '12px' }}>
            ← Back to Interviews
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>{interview?.title} - Responses</h2>
              <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
                Review and evaluate student interview responses
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <input 
                type="checkbox" 
                id="showKeyPoints" 
                checked={showKeyPoints}
                onChange={(e) => setShowKeyPoints(e.target.checked)}
                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
              />
              <label htmlFor="showKeyPoints" style={{ margin: 0, cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: '#2d3748', userSelect: 'none' }}>
                Show Key Points
              </label>
            </div>
          </div>
        </div>

        {responses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>No responses yet.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '24px' }}>
            <div style={{ background: '#f7fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', maxHeight: '800px', overflowY: 'auto' }}>
              <h3 style={{ color: '#2d3748', fontSize: '16px', marginBottom: '16px' }}>
                Students ({studentsList.length})
              </h3>
              {studentsList.map(({ student, responses: studentResponses }) => {
                const latestResponse = studentResponses[0];
                const hasMultipleAttempts = studentResponses.length > 1;
                
                return (
                  <div
                    key={student._id}
                    style={{
                      padding: '16px',
                      background: 'white',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      border: selectedStudent?._id === student._id ? '2px solid #667eea' : '1px solid #e2e8f0',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ marginBottom: '8px' }}>
                      <h4 style={{ margin: '0 0 4px 0', color: '#2d3748', fontSize: '15px' }}>{student.name}</h4>
                      <p style={{ margin: 0, color: '#718096', fontSize: '13px' }}>{student.email}</p>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', marginBottom: '8px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: latestResponse.status === 'submitted' ? '#c6f6d5' : 
                                   latestResponse.status === 'in_progress' ? '#feebc8' :
                                   latestResponse.status === 'timed_out' ? '#fed7d7' : '#e2e8f0',
                        color: latestResponse.status === 'submitted' ? '#22543d' :
                              latestResponse.status === 'in_progress' ? '#744210' :
                              latestResponse.status === 'timed_out' ? '#742a2a' : '#2d3748'
                      }}>
                        {getStatusBadge(latestResponse.status).text}
                      </span>
                      {latestResponse.rating && (
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#667eea' }}>⭐ {latestResponse.rating}/10</span>
                      )}
                    </div>
                    {hasMultipleAttempts && (
                      <div style={{ marginTop: '8px', padding: '6px 10px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #f59e0b' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#92400e' }}>
                          🔄 {studentResponses.length} submission{studentResponses.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                    {latestResponse.submittedAt && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#718096' }}>
                        <div>Latest: {new Date(latestResponse.submittedAt).toLocaleString()}</div>
                        {latestResponse.startedAt && latestResponse.submittedAt && (
                          <div style={{ marginTop: '4px', fontWeight: 600, color: '#805ad5' }}>
                            ⏱️ Time taken: {formatInterviewDuration(latestResponse.startedAt, latestResponse.submittedAt)}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Show all submissions for this student */}
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {studentResponses.map((response, idx) => (
                        <button
                          key={response._id}
                          onClick={() => handleViewResponse(response, student)}
                          className="btn btn-primary"
                          style={{
                            padding: '8px 12px',
                            fontSize: '12px',
                            width: '100%',
                            background: selectedResponse?._id === response._id ? '#667eea' : 'white',
                            color: selectedResponse?._id === response._id ? 'white' : '#667eea',
                            border: `1.5px solid ${selectedResponse?._id === response._id ? '#667eea' : '#c7d2fe'}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span>
                            {idx === 0 ? '📋 Latest' : `🔄 Attempt ${studentResponses.length - idx}`}
                          </span>
                          <span style={{ fontSize: '10px' }}>
                            {response.submittedAt ? new Date(response.submittedAt).toLocaleDateString() : 'Not submitted'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', maxHeight: '800px', overflowY: 'auto' }}>
              {selectedResponse ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>
                    <div>
                      <h3 style={{ margin: '0 0 6px 0', color: '#2d3748', fontSize: '18px' }}>
                        {selectedResponse.student?.name || selectedStudent?.name}'s Response
                      </h3>
                      {selectedStudent && groupedResponses[selectedStudent._id] && groupedResponses[selectedStudent._id].responses.length > 1 && (
                        <p style={{ margin: 0, fontSize: '13px', color: '#718096' }}>
                          Attempt {groupedResponses[selectedStudent._id].responses.findIndex(r => r._id === selectedResponse._id) + 1} of {groupedResponses[selectedStudent._id].responses.length}
                          {' • '}
                          {selectedResponse.submittedAt ? new Date(selectedResponse.submittedAt).toLocaleString() : 'Not submitted yet'}
                          {selectedResponse.startedAt && selectedResponse.submittedAt && (
                            <span style={{ color: '#805ad5', fontWeight: 600 }}>
                              {' • ⏱️ '}
                              {formatInterviewDuration(selectedResponse.startedAt, selectedResponse.submittedAt)}
                            </span>
                          )}
                        </p>
                      )}
                      {selectedStudent && groupedResponses[selectedStudent._id] && groupedResponses[selectedStudent._id].responses.length === 1 && (
                        <p style={{ margin: 0, fontSize: '13px', color: '#718096' }}>
                          {selectedResponse.submittedAt ? new Date(selectedResponse.submittedAt).toLocaleString() : 'Not submitted yet'}
                          {selectedResponse.startedAt && selectedResponse.submittedAt && (
                            <span style={{ color: '#805ad5', fontWeight: 600 }}>
                              {' • ⏱️ '}
                              {formatInterviewDuration(selectedResponse.startedAt, selectedResponse.submittedAt)}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                        onClick={handleRegenerateAI}
                        disabled={regeneratingAI}
                        style={{
                          padding: '6px 12px',
                          fontSize: '13px',
                          background: regeneratingAI ? '#cbd5e0' : '#9f7aea',
                          color: 'white',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: regeneratingAI ? 'not-allowed' : 'pointer',
                          borderRadius: '4px',
                          opacity: regeneratingAI ? 0.6 : 1
                        }}
                      >
                        🤖 {regeneratingAI ? 'Regenerating...' : 'Regenerate AI'}
                      </button>
                      <button
                        onClick={handleDownloadReport}
                        className="btn btn-primary"
                        style={{ 
                          padding: '6px 12px', 
                          fontSize: '13px',
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        📄 Download Report
                      </button>
                      <button
                        onClick={handleCopyReport}
                        className="btn btn-secondary"
                        style={{ 
                          padding: '6px 12px', 
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        📋 Copy Report
                      </button>
                      <span style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: selectedResponse.status === 'submitted' ? '#c6f6d5' :
                                   selectedResponse.status === 'in_progress' ? '#feebc8' :
                                   selectedResponse.status === 'timed_out' ? '#fed7d7' : '#e2e8f0',
                        color: selectedResponse.status === 'submitted' ? '#22543d' :
                              selectedResponse.status === 'in_progress' ? '#744210' :
                              selectedResponse.status === 'timed_out' ? '#742a2a' : '#2d3748'
                      }}>
                        {getStatusBadge(selectedResponse.status).text}
                      </span>
                      <button 
                        onClick={() => handleDeleteResponse(selectedResponse._id)} 
                        className="btn btn-danger"
                        style={{ padding: '6px 12px', fontSize: '13px' }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>

                  {/* Overall Interview Performance - Admin View */}
                  {user?.role === 'admin' && selectedResponse.overallFeedback && (selectedResponse.status === 'submitted' || selectedResponse.status === 'timed_out') && (
                    <div style={{ marginBottom: '24px', padding: '24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)' }}>
                      <div style={{ background: 'white', borderRadius: '10px', padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>
                          <h4 style={{ color: '#667eea', fontSize: '18px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '24px' }}>🎯</span> Overall Interview Performance
                          </h4>
                          {selectedResponse.overallFeedback.overallScore !== undefined && (
                            <div style={{ 
                              fontSize: '32px', 
                              fontWeight: 800, 
                              background: selectedResponse.overallFeedback.overallScore >= 70 ? 
                                'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : 
                                selectedResponse.overallFeedback.overallScore >= 50 ? 
                                'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 
                                'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text'
                            }}>
                              {Math.round(selectedResponse.overallFeedback.overallScore)}/100
                            </div>
                          )}
                        </div>

                        {/* Admin View - Professional Report Language */}
                        {selectedResponse.overallFeedback.adminSummary && (
                          <div style={{ marginBottom: '20px' }}>
                            <strong style={{ display: 'block', fontSize: '14px', color: '#667eea', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              📋 Performance Summary
                            </strong>
                            <p style={{ margin: 0, fontSize: '15px', color: '#2d3748', lineHeight: 1.7, padding: '12px', background: '#f7fafc', borderRadius: '8px', borderLeft: '3px solid #667eea' }}>
                              {selectedResponse.overallFeedback.adminSummary}
                            </p>
                          </div>
                        )}

                        {selectedResponse.overallFeedback.adminStrengths && selectedResponse.overallFeedback.adminStrengths.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <strong style={{ display: 'block', fontSize: '14px', color: '#16a34a', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              ✓ Key Strengths Demonstrated
                            </strong>
                            <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '15px', color: '#15803d', background: '#f0fdf4', padding: '16px 16px 16px 40px', borderRadius: '8px', border: '2px solid #86efac' }}>
                              {selectedResponse.overallFeedback.adminStrengths.map((strength, idx) => (
                                <li key={idx} style={{ marginBottom: '6px', lineHeight: 1.6 }}>{strength}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {selectedResponse.overallFeedback.adminImprovements && selectedResponse.overallFeedback.adminImprovements.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <strong style={{ display: 'block', fontSize: '14px', color: '#d97706', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              💡 Areas for Development
                            </strong>
                            <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '15px', color: '#92400e', background: '#fffbeb', padding: '16px 16px 16px 40px', borderRadius: '8px', border: '2px solid #fcd34d' }}>
                              {selectedResponse.overallFeedback.adminImprovements.map((improvement, idx) => (
                                <li key={idx} style={{ marginBottom: '6px', lineHeight: 1.6 }}>{improvement}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {selectedResponse.overallFeedback.missedTerminology && selectedResponse.overallFeedback.missedTerminology.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <strong style={{ display: 'block', fontSize: '14px', color: '#dc2626', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              🎓 Important Terminology Missed
                            </strong>
                            <div style={{ background: '#fef2f2', padding: '16px', borderRadius: '8px', border: '2px solid #fca5a5' }}>
                              <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '15px', color: '#991b1b' }}>
                                {selectedResponse.overallFeedback.missedTerminology.map((term, idx) => (
                                  <li key={idx} style={{ marginBottom: '6px', lineHeight: 1.6 }}><strong>{term}</strong></li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}

                        {selectedResponse.overallFeedback.adminFeedforward && (
                          <div style={{ marginBottom: '0' }}>
                            <strong style={{ display: 'block', fontSize: '14px', color: '#7c3aed', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              🎯 Professional Recommendations
                            </strong>
                            <div style={{ 
                              padding: '16px', 
                              background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', 
                              borderRadius: '8px', 
                              border: '2px solid #c084fc',
                              fontSize: '15px',
                              color: '#5b21b6',
                              lineHeight: 1.7
                            }}>
                              {selectedResponse.overallFeedback.adminFeedforward}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Overall Performance Summary */}
                  {performanceMetrics && (selectedResponse.status === 'submitted' || selectedResponse.status === 'timed_out') && (
                    <div style={{ 
                      marginBottom: '24px', 
                      padding: '24px', 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                      borderRadius: '12px', 
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)' 
                    }}>
                      <div style={{ background: 'white', borderRadius: '10px', padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>
                          <h4 style={{ color: '#667eea', fontSize: '18px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '24px' }}>🎯</span> Overall Interview Performance
                          </h4>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                          {/* Overall AI Score */}
                          {performanceMetrics.overallScore !== undefined && performanceMetrics.overallScore !== null && (
                            <div style={{ 
                              padding: '16px', 
                              background: performanceMetrics.overallScore >= 70 ? '#f0fdf4' : 
                                         performanceMetrics.overallScore >= 50 ? '#fffbeb' : '#fef2f2',
                              border: performanceMetrics.overallScore >= 70 ? '2px solid #86efac' :
                                     performanceMetrics.overallScore >= 50 ? '2px solid #fcd34d' : '2px solid #fca5a5',
                              borderRadius: '8px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '12px', color: '#718096', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                AI Overall Score
                              </div>
                              <div style={{ 
                                fontSize: '36px', 
                                fontWeight: 800,
                                color: performanceMetrics.overallScore >= 70 ? '#16a34a' : 
                                       performanceMetrics.overallScore >= 50 ? '#d97706' : '#dc2626'
                              }}>
                                {Math.round(performanceMetrics.overallScore)}
                                <span style={{ fontSize: '20px' }}>/100</span>
                              </div>
                            </div>
                          )}

                          {/* Average AI Score */}
                          {performanceMetrics.avgAIScore !== null && (
                            <div style={{ 
                              padding: '16px', 
                              background: '#faf5ff',
                              border: '2px solid #d8b4fe',
                              borderRadius: '8px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '12px', color: '#718096', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Avg Question Score
                              </div>
                              <div style={{ fontSize: '36px', fontWeight: 800, color: '#7c3aed' }}>
                                {Math.round(performanceMetrics.avgAIScore)}
                                <span style={{ fontSize: '20px' }}>/100</span>
                              </div>
                            </div>
                          )}

                          {/* Admin Rating */}
                          {performanceMetrics.adminRating && (
                            <div style={{ 
                              padding: '16px', 
                              background: '#ecfccb',
                              border: '2px solid #bef264',
                              borderRadius: '8px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '12px', color: '#718096', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Admin Rating
                              </div>
                              <div style={{ fontSize: '36px', fontWeight: 800, color: '#65a30d' }}>
                                {performanceMetrics.adminRating}
                                <span style={{ fontSize: '20px' }}>/10</span>
                              </div>
                            </div>
                          )}

                          {/* Questions Answered */}
                          <div style={{ 
                            padding: '16px', 
                            background: '#dbeafe',
                            border: '2px solid #93c5fd',
                            borderRadius: '8px',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '12px', color: '#718096', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Questions Answered
                            </div>
                            <div style={{ fontSize: '36px', fontWeight: 800, color: '#1d4ed8' }}>
                              {performanceMetrics.answeredCount}
                              <span style={{ fontSize: '20px' }}>/{performanceMetrics.totalQuestions}</span>
                            </div>
                          </div>

                          {/* Follow-up Questions */}
                          {performanceMetrics.followUpCount > 0 && (
                            <div style={{ 
                              padding: '16px', 
                              background: '#fce7f3',
                              border: '2px solid #f9a8d4',
                              borderRadius: '8px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '12px', color: '#718096', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Follow-ups Answered
                              </div>
                              <div style={{ fontSize: '36px', fontWeight: 800, color: '#db2777' }}>
                                {performanceMetrics.answeredFollowUps}
                                <span style={{ fontSize: '20px' }}>/{performanceMetrics.followUpCount}</span>
                              </div>
                            </div>
                          )}

                          {/* Completion Rate */}
                          <div style={{ 
                            padding: '16px', 
                            background: '#ffedd5',
                            border: '2px solid #fdba74',
                            borderRadius: '8px',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '12px', color: '#718096', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Completion Rate
                            </div>
                            <div style={{ fontSize: '36px', fontWeight: 800, color: '#ea580c' }}>
                              {Math.round((performanceMetrics.answeredCount / performanceMetrics.totalQuestions) * 100)}
                              <span style={{ fontSize: '20px' }}>%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedResponse.status === 'submitted' || selectedResponse.status === 'timed_out' ? (
                    <>
                      {/* Video Recording Section */}
                      <div style={{ marginBottom: '24px', padding: '20px', background: '#f7fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h4 style={{ color: '#667eea', fontSize: '16px', marginBottom: '12px' }}>📹 Interview Recording</h4>
                        {selectedResponse.videoRecording ? (
                          <div style={{ display: 'grid', gridTemplateColumns: selectedResponse.videoChapters && selectedResponse.videoChapters.length > 0 ? '1fr 300px' : '1fr', gap: '16px' }}>
                            {/* Video Player */}
                            <div>
                              {loadingVideo ? (
                                <div style={{ 
                                  width: '100%', 
                                  height: '500px', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  background: '#000',
                                  borderRadius: '8px'
                                }}>
                                  <p style={{ color: 'white' }}>Loading video...</p>
                                </div>
                              ) : videoUrl ? (
                                <video
                                  ref={videoRef}
                                  controls
                                  onTimeUpdate={handleVideoTimeUpdate}
                                  style={{
                                    width: '100%',
                                    maxHeight: '500px',
                                    borderRadius: '8px',
                                    background: '#000',
                                    objectFit: 'contain'
                                  }}
                                  key={videoUrl} // Force re-render when URL changes
                                >
                                  <source src={videoUrl} type="video/webm" />
                                  <source src={videoUrl} type="video/mp4" />
                                  Your browser does not support the video tag.
                                </video>
                              ) : (
                                <div style={{ 
                                  width: '100%', 
                                  height: '500px', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  background: '#000',
                                  borderRadius: '8px'
                                }}>
                                  <p style={{ color: 'white' }}>Video not available</p>
                                </div>
                              )}
                              <p style={{ marginTop: '8px', fontSize: '12px', color: '#718096', fontStyle: 'italic' }}>
                                💡 Watch the complete interview recording to evaluate the candidate's communication and presentation skills.
                              </p>
                            </div>
                            
                            {/* Video Chapters Sidebar */}
                            {selectedResponse.videoChapters && selectedResponse.videoChapters.length > 0 && (
                              <div style={{ 
                                background: 'white', 
                                borderRadius: '8px', 
                                border: '1px solid #e2e8f0',
                                maxHeight: '500px',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                scrollBehavior: 'smooth'
                              }}
                              className="chapters-sidebar">
                                <style>{`
                                  .chapters-sidebar::-webkit-scrollbar {
                                    width: 8px;
                                  }
                                  .chapters-sidebar::-webkit-scrollbar-track {
                                    background: #f1f1f1;
                                    border-radius: 4px;
                                  }
                                  .chapters-sidebar::-webkit-scrollbar-thumb {
                                    background: #cbd5e0;
                                    border-radius: 4px;
                                  }
                                  .chapters-sidebar::-webkit-scrollbar-thumb:hover {
                                    background: #a0aec0;
                                  }
                                `}</style>
                                <div style={{ 
                                  padding: '12px 16px', 
                                  borderBottom: '2px solid #e2e8f0',
                                  position: 'sticky',
                                  top: 0,
                                  background: 'white',
                                  zIndex: 1
                                }}>
                                  <h5 style={{ margin: 0, color: '#2d3748', fontSize: '14px', fontWeight: 600 }}>
                                    📑 Chapters ({selectedResponse.videoChapters.length})
                                  </h5>
                                </div>
                                <div>
                                  {selectedResponse.videoChapters.map((chapter, index) => (
                                    <div
                                      key={index}
                                      onClick={() => handleChapterClick(index)}
                                      style={{
                                        padding: '12px 16px',
                                        borderBottom: index < selectedResponse.videoChapters.length - 1 ? '1px solid #e2e8f0' : 'none',
                                        cursor: 'pointer',
                                        background: currentChapter === index ? '#f0f4ff' : 'white',
                                        borderLeft: currentChapter === index ? '3px solid #667eea' : '3px solid transparent',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                          background: '#f7fafc'
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        if (currentChapter !== index) {
                                          e.currentTarget.style.background = '#f7fafc';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (currentChapter !== index) {
                                          e.currentTarget.style.background = 'white';
                                        }
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                        <span style={{ 
                                          fontSize: '12px', 
                                          fontWeight: 600, 
                                          color: currentChapter === index ? '#667eea' : '#718096',
                                          minWidth: '45px',
                                          fontFamily: 'monospace'
                                        }}>
                                          {formatChapterTime(chapter.timestamp)}
                                        </span>
                                        <div style={{ flex: 1 }}>
                                          <div style={{ 
                                            fontSize: '11px', 
                                            fontWeight: 600,
                                            color: chapter.isFollowUp ? '#9f7aea' : '#667eea',
                                            marginBottom: '4px'
                                          }}>
                                            {chapter.isFollowUp 
                                              ? `Q${chapter.parentQuestionNumber} Follow-up` 
                                              : `Question ${chapter.questionNumber}`}
                                          </div>
                                          <div style={{ 
                                            fontSize: '12px', 
                                            color: '#2d3748',
                                            lineHeight: 1.4,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical'
                                          }}>
                                            {chapter.questionText}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p style={{ color: '#718096', fontSize: '14px', padding: '20px', textAlign: 'center', background: '#fff', borderRadius: '8px' }}>
                            No video recording available for this interview.
                          </p>
                        )}
                      </div>

                      <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ color: '#667eea', fontSize: '16px', marginBottom: '16px' }}>Answers (Speech-to-Text Transcript)</h4>
                        {interview.questions.map((q, index) => {
                          if (!q || !q.questionId) return null;
                          const answer = getAnswerForQuestion(selectedResponse, q.questionId._id || q.questionId);
                          return (
                            <div key={q.questionId._id || index} style={{ marginBottom: '24px', padding: '16px', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                              <div style={{ marginBottom: '12px' }}>
                                <strong style={{ color: '#2d3748', fontSize: '14px' }}>Q{index + 1}: </strong>
                                <span style={{ color: '#2d3748', fontSize: '14px' }}>{q.questionId.question}</span>
                              </div>
                              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                                <span style={{
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  background: q.questionId.difficulty === 'Easy' ? '#c6f6d5' : q.questionId.difficulty === 'Medium' ? '#feebc8' : '#fed7d7',
                                  color: q.questionId.difficulty === 'Easy' ? '#22543d' : q.questionId.difficulty === 'Medium' ? '#744210' : '#742a2a'
                                }}>
                                  {q.questionId.difficulty}
                                </span>
                                <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, background: '#bee3f8', color: '#2c5282' }}>
                                  {q.questionId.questionType}
                                </span>
                              </div>
                              {answer ? (
                                <div style={{ marginTop: '12px' }}>
                                  {/* AI Processing Status */}
                                  {interview.showAIEvaluation && answer.aiProcessingStatus && answer.aiProcessingStatus !== 'not_required' && (
                                    <div style={{ 
                                      marginBottom: '12px', 
                                      padding: '10px 12px', 
                                      background: answer.aiProcessingStatus === 'completed' ? '#f0fff4' : 
                                                 answer.aiProcessingStatus === 'processing' ? '#fffaf0' :
                                                 answer.aiProcessingStatus === 'failed' ? '#fff5f5' : '#f7fafc',
                                      borderRadius: '6px',
                                      border: `1px solid ${answer.aiProcessingStatus === 'completed' ? '#9ae6b4' : 
                                                           answer.aiProcessingStatus === 'processing' ? '#fbd38d' :
                                                           answer.aiProcessingStatus === 'failed' ? '#fc8181' : '#e2e8f0'}`
                                    }}>
                                      <span style={{ 
                                        fontSize: '12px', 
                                        fontWeight: 600,
                                        color: answer.aiProcessingStatus === 'completed' ? '#22543d' : 
                                               answer.aiProcessingStatus === 'processing' ? '#744210' :
                                               answer.aiProcessingStatus === 'failed' ? '#742a2a' : '#2d3748'
                                      }}>
                                        {answer.aiProcessingStatus === 'completed' && '✓ AI Processing Complete'}
                                        {answer.aiProcessingStatus === 'processing' && '⏳ AI Processing...'}
                                        {answer.aiProcessingStatus === 'failed' && '⚠️ AI Processing Failed'}
                                        {answer.aiProcessingStatus === 'pending' && '⏸️ AI Processing Pending'}
                                      </span>
                                    </div>
                                  )}

                                  {/* Display cleaned answer if available, otherwise show original */}
                                  <>
                                    <strong style={{ display: 'block', marginBottom: '6px', color: '#667eea', fontSize: '12px' }}>
                                      {answer.cleanedAnswer ? '🤖 ANSWER (AI-Cleaned):' : 'ANSWER:'}
                                    </strong>
                                    {q.questionId.questionType === 'MCQ' && q.questionId.options ? (
                                      <p style={{ margin: 0, padding: '12px', background: 'white', borderRadius: '6px', color: '#2d3748', fontSize: '14px' }}>
                                        {q.questionId.options[parseInt(answer.answer)] || answer.answer}
                                      </p>
                                    ) : (
                                      <div style={{ 
                                        padding: '12px', 
                                        background: answer.cleanedAnswer ? 'linear-gradient(to right, #f0f4ff, #faf5ff)' : 'white', 
                                        borderRadius: '6px', 
                                        color: '#2d3748', 
                                        fontSize: '16px', 
                                        whiteSpace: 'pre-wrap',
                                        borderLeft: answer.cleanedAnswer ? '3px solid #667eea' : 'none'
                                      }}>
                                        {answer.cleanedAnswer || answer.answer || 'No answer provided'}
                                      </div>
                                    )}
                                  </>

                                  {/* AI Assessment */}
                                  {answer.aiAssessment && (
                                    <div style={{ marginTop: '16px', padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '2px solid #86efac' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                        <strong style={{ color: '#166534', fontSize: '14px' }}>🎯 AI ASSESSMENT</strong>
                                        <span style={{ 
                                          fontSize: '26px', 
                                          fontWeight: 700, 
                                          color: answer.aiAssessment.score >= 70 ? '#16a34a' : answer.aiAssessment.score >= 50 ? '#ca8a04' : '#dc2626',
                                          padding: '4px 12px',
                                          background: answer.aiAssessment.score >= 70 ? '#dcfce7' : answer.aiAssessment.score >= 50 ? '#fef3c7' : '#fee2e2',
                                          borderRadius: '6px'
                                        }}>
                                          {answer.aiAssessment.score}/100
                                        </span>
                                      </div>
                                      
                                      <div style={{ marginBottom: '12px' }}>
                                        <strong style={{ display: 'block', fontSize: '11px', color: '#166534', marginBottom: '4px' }}>FEEDBACK:</strong>
                                        <p style={{ margin: 0, fontSize: '15px', color: '#15803d', lineHeight: 1.6 }}>{answer.aiAssessment.feedback}</p>
                                      </div>

                                      {answer.aiAssessment.strengths && answer.aiAssessment.strengths.length > 0 && (
                                        <div style={{ marginBottom: '12px' }}>
                                          <strong style={{ display: 'block', fontSize: '14px', color: '#166534', marginBottom: '6px' }}>✓ STRENGTHS:</strong>
                                          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '15px', color: '#15803d' }}>
                                            {answer.aiAssessment.strengths.map((strength, idx) => (
                                              <li key={idx} style={{ marginBottom: '3px' }}>{strength}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}

                                      {answer.aiAssessment.improvements && answer.aiAssessment.improvements.length > 0 && (
                                        <div>
                                          <strong style={{ display: 'block', fontSize: '14px', color: '#166534', marginBottom: '6px' }}>💡 AREAS FOR IMPROVEMENT:</strong>
                                          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '15px', color: '#15803d' }}>
                                            {answer.aiAssessment.improvements.map((improvement, idx) => (
                                              <li key={idx} style={{ marginBottom: '3px' }}>{improvement}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}

                                      {answer.aiAssessment.aiModelUsed && (
                                        <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #86efac' }}>
                                          <span style={{ fontSize: '11px', color: '#15803d', fontWeight: 600 }}>
                                            🤖 AI Model: {answer.aiAssessment.aiModelUsed === 'gemini' ? 'Google Gemini' : 'Deepseek AI'}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {answer.timeSpent && (
                                    <span style={{ display: 'inline-block', marginTop: '8px', fontSize: '12px', color: '#718096' }}>
                                      ⏱️ Time spent: {Math.round(answer.timeSpent / 1000)} seconds
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <p style={{ margin: 0, padding: '12px', background: '#fff5f5', borderRadius: '6px', color: '#742a2a', fontSize: '14px' }}>Not answered</p>
                              )}

                              {showKeyPoints && q.questionId.interviewerPoints && q.questionId.interviewerPoints.length > 0 && (
                                <div style={{ marginTop: '12px', padding: '12px', background: '#e7f3ff', borderRadius: '6px', borderLeft: '3px solid #667eea' }}>
                                  <strong style={{ display: 'block', marginBottom: '8px', color: '#2c5282', fontSize: '12px' }}>WHAT THE INTERVIEWER IS LOOKING FOR:</strong>
                                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    {q.questionId.interviewerPoints.map((point, idx) => (
                                      <li key={idx} style={{ color: '#2d3748', fontSize: '13px', marginBottom: '4px' }}>{point}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Follow-up Questions for this main question */}
                              {selectedResponse.responses && selectedResponse.responses.filter(r => {
                                const parentId = r.parentQuestionId?._id || r.parentQuestionId;
                                const questionId = q.questionId._id || q.questionId;
                                return r.isFollowUp && parentId === questionId;
                              }).length > 0 && (
                                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '2px dashed #d6bcfa' }}>
                                  {selectedResponse.responses.filter(r => {
                                    const parentId = r.parentQuestionId?._id || r.parentQuestionId;
                                    const questionId = q.questionId._id || q.questionId;
                                    return r.isFollowUp && parentId === questionId;
                                  }).map((followUp, fuIdx) => (
                                    <div 
                                      key={fuIdx}
                                      style={{ 
                                        background: '#faf5ff',
                                        padding: '12px',
                                        borderRadius: '6px',
                                        marginBottom: fuIdx < selectedResponse.responses.filter(r => {
                                          const parentId = r.parentQuestionId?._id || r.parentQuestionId;
                                          const questionId = q.questionId._id || q.questionId;
                                          return r.isFollowUp && parentId === questionId;
                                        }).length - 1 ? '12px' : '0',
                                        border: '2px solid #d6bcfa'
                                      }}
                                    >
                                      <div style={{ marginBottom: '8px' }}>
                                        <span style={{ 
                                          display: 'inline-block',
                                          padding: '4px 10px', 
                                          borderRadius: '12px', 
                                          fontSize: '11px', 
                                          fontWeight: 700, 
                                          background: '#9f7aea', 
                                          color: 'white',
                                          marginBottom: '6px'
                                        }}>
                                          🤖 AI Follow-up {fuIdx + 1}
                                        </span>
                                        <strong style={{ display: 'block', color: '#44337a', fontSize: '14px', marginTop: '6px' }}>
                                          {followUp.followUpQuestion}
                                        </strong>
                                      </div>

                                      <div style={{ marginTop: '10px' }}>
                                        <strong style={{ display: 'block', marginBottom: '6px', color: '#6b46c1', fontSize: '11px' }}>
                                          {followUp.cleanedAnswer ? '🤖 ANSWER (AI-Cleaned):' : 'ANSWER:'}
                                        </strong>
                                        <div style={{ 
                                          margin: 0, 
                                          padding: '12px', 
                                          background: followUp.cleanedAnswer ? 'linear-gradient(135deg, #9f7aea15 0%, #6b46c115 100%)' : 'white', 
                                          borderRadius: '6px', 
                                          color: '#2d3748', 
                                          fontSize: '14px',
                                          lineHeight: 1.6,
                                          whiteSpace: 'pre-wrap',
                                          border: followUp.cleanedAnswer ? '1px solid #9f7aea30' : '1px solid #e2e8f0'
                                        }}>
                                          {followUp.cleanedAnswer || followUp.answer || 'No answer provided'}
                                        </div>
                                      </div>

                                      {followUp.aiAssessment && (
                                        <div style={{ 
                                          marginTop: '12px',
                                          background: '#f0fdf4',
                                          padding: '12px',
                                          borderRadius: '6px',
                                          border: '1px solid #c4b5fd'
                                        }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <strong style={{ color: '#166534', fontSize: '14px' }}>🎯 SCORE</strong>
                                            <span style={{ 
                                              fontSize: '24px',
                                              fontWeight: 700,
                                              color: followUp.aiAssessment.score >= 70 ? '#16a34a' : followUp.aiAssessment.score >= 50 ? '#ca8a04' : '#dc2626'
                                            }}>
                                              {followUp.aiAssessment.score}/100
                                            </span>
                                          </div>
                                          
                                          {followUp.aiAssessment.adminFeedback && (
                                            <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#15803d', lineHeight: 1.5 }}>
                                              {followUp.aiAssessment.adminFeedback}
                                            </p>
                                          )}

                                          {followUp.aiAssessment.adminStrengths && followUp.aiAssessment.adminStrengths.length > 0 && (
                                            <div style={{ marginBottom: '8px' }}>
                                              <strong style={{ fontSize: '14px', color: '#166534', display: 'block', marginBottom: '4px' }}>
                                                ✓ Strengths:
                                              </strong>
                                              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#15803d' }}>
                                                {followUp.aiAssessment.adminStrengths.map((strength, idx) => (
                                                  <li key={idx}>{strength}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}

                                          {followUp.aiAssessment.adminImprovements && followUp.aiAssessment.adminImprovements.length > 0 && (
                                            <div>
                                              <strong style={{ fontSize: '14px', color: '#166534', display: 'block', marginBottom: '4px' }}>
                                                💡 Areas to Improve:
                                              </strong>
                                              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#15803d' }}>
                                                {followUp.aiAssessment.adminImprovements.map((improvement, idx) => (
                                                  <li key={idx}>{improvement}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}

                                          {followUp.aiAssessment.aiModelUsed && (
                                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #c4b5fd' }}>
                                              <span style={{ fontSize: '11px', color: '#5b21b6', fontWeight: 600 }}>
                                                🤖 AI Model: {followUp.aiAssessment.aiModelUsed === 'gemini' ? 'Google Gemini' : 'Deepseek AI'}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {followUp.timeSpent && (
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b46c1' }}>
                                          ⏱️ Time spent: {Math.round(followUp.timeSpent / 1000)} seconds
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* All Follow-Up Questions for this Question */}
                              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '2px dashed #e2e8f0' }}>
                                <button
                                  onClick={() => fetchFollowUpsForQuestion(q.questionId._id)}
                                  disabled={loadingFollowUps[q.questionId._id]}
                                  style={{
                                    padding: '8px 16px',
                                    background: expandedQuestions[q.questionId._id] ? '#9f7aea' : '#667eea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: loadingFollowUps[q.questionId._id] ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    opacity: loadingFollowUps[q.questionId._id] ? 0.6 : 1,
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!loadingFollowUps[q.questionId._id]) {
                                      e.currentTarget.style.transform = 'translateY(-1px)';
                                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                >
                                  {loadingFollowUps[q.questionId._id] ? '⏳ Loading...' : 
                                   expandedQuestions[q.questionId._id] ? '🔼 Hide All Follow-ups for This Question' : 
                                   '🔽 View All Follow-ups for This Question'}
                                </button>

                                {expandedQuestions[q.questionId._id] && questionFollowUps[q.questionId._id] && (
                                  <div style={{ marginTop: '12px' }}>
                                    {questionFollowUps[q.questionId._id].length === 0 ? (
                                      <p style={{ 
                                        color: '#718096', 
                                        fontSize: '13px', 
                                        fontStyle: 'italic',
                                        padding: '12px',
                                        background: '#f7fafc',
                                        borderRadius: '6px',
                                        margin: 0
                                      }}>
                                        No follow-up questions have been asked for this question yet.
                                      </p>
                                    ) : (
                                      <>
                                        <div style={{ 
                                          marginBottom: '12px',
                                          padding: '8px 12px',
                                          background: 'linear-gradient(to right, #f0f4ff, #faf5ff)',
                                          borderRadius: '6px',
                                          fontSize: '13px',
                                          color: '#5b21b6',
                                          fontWeight: 600
                                        }}>
                                          📚 {questionFollowUps[q.questionId._id].length} follow-up question(s) asked across all students
                                        </div>
                                        {questionFollowUps[q.questionId._id].map((followUpDoc, fuDocIdx) => (
                                          <div 
                                            key={followUpDoc._id}
                                            style={{ 
                                              background: followUpDoc.savedToBank ? '#f0fdf4' : 'white',
                                              padding: '12px',
                                              borderRadius: '6px',
                                              marginBottom: fuDocIdx < questionFollowUps[q.questionId._id].length - 1 ? '10px' : '0',
                                              border: followUpDoc.savedToBank ? '2px solid #86efac' : '1px solid #e2e8f0',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                            }}
                                          >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                              <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: 500, marginBottom: '8px', lineHeight: 1.5 }}>
                                                  {followUpDoc.followUpText}
                                                </div>
                                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                  <span style={{ 
                                                    fontSize: '11px', 
                                                    padding: '3px 8px', 
                                                    borderRadius: '10px', 
                                                    background: '#e0e7ff', 
                                                    color: '#3730a3',
                                                    fontWeight: 600
                                                  }}>
                                                    {followUpDoc.difficulty}
                                                  </span>
                                                  <span style={{ 
                                                    fontSize: '11px', 
                                                    padding: '3px 8px', 
                                                    borderRadius: '10px', 
                                                    background: '#fef3c7', 
                                                    color: '#78350f',
                                                    fontWeight: 600
                                                  }}>
                                                    Used {followUpDoc.usageCount}x
                                                  </span>
                                                  {followUpDoc.aiModelUsed && (
                                                    <span style={{ 
                                                      fontSize: '11px', 
                                                      padding: '3px 8px', 
                                                      borderRadius: '10px', 
                                                      background: '#ddd6fe', 
                                                      color: '#5b21b6',
                                                      fontWeight: 600
                                                    }}>
                                                      {followUpDoc.aiModelUsed === 'gemini' ? '🤖 Gemini' : '🤖 Deepseek'}
                                                    </span>
                                                  )}
                                                  {followUpDoc.savedToBank && (
                                                    <span style={{ 
                                                      fontSize: '11px', 
                                                      padding: '3px 8px', 
                                                      borderRadius: '10px', 
                                                      background: '#86efac', 
                                                      color: '#166534',
                                                      fontWeight: 700
                                                    }}>
                                                      ✓ Saved to Bank
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              {!followUpDoc.savedToBank && (
                                                <button
                                                  onClick={() => handleSaveFollowUpToBank(followUpDoc._id, q.questionId._id)}
                                                  disabled={savingFollowUp[followUpDoc._id]}
                                                  style={{
                                                    padding: '6px 12px',
                                                    background: savingFollowUp[followUpDoc._id] ? '#cbd5e0' : '#10b981',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '5px',
                                                    cursor: savingFollowUp[followUpDoc._id] ? 'not-allowed' : 'pointer',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                    transition: 'all 0.2s'
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    if (!savingFollowUp[followUpDoc._id]) {
                                                      e.currentTarget.style.background = '#059669';
                                                      e.currentTarget.style.transform = 'scale(1.05)';
                                                    }
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!savingFollowUp[followUpDoc._id]) {
                                                      e.currentTarget.style.background = '#10b981';
                                                      e.currentTarget.style.transform = 'scale(1)';
                                                    }
                                                  }}
                                                >
                                                  {savingFollowUp[followUpDoc._id] ? '⏳ Saving...' : '💾 Save to Bank'}
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Overall Interview Feedback - Student View Only (Admin view is at the top) */}
                      {user?.role !== 'admin' && selectedResponse.overallFeedback && (
                        <div style={{ marginBottom: '24px', padding: '24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)' }}>
                          <div style={{ background: 'white', borderRadius: '10px', padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>
                              <h4 style={{ color: '#667eea', fontSize: '18px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '24px' }}>🎯</span> Overall Interview Performance
                              </h4>
                              {selectedResponse.overallFeedback.overallScore !== undefined && (
                                <div style={{ 
                                  fontSize: '32px', 
                                  fontWeight: 800, 
                                  background: selectedResponse.overallFeedback.overallScore >= 70 ? 
                                    'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : 
                                    selectedResponse.overallFeedback.overallScore >= 50 ? 
                                    'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 
                                    'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  backgroundClip: 'text'
                                }}>
                                  {Math.round(selectedResponse.overallFeedback.overallScore)}/100
                                </div>
                              )}
                            </div>

                            {/* Student View - Personalized First-Person Feedback */}
                                {selectedResponse.overallFeedback.studentSummary && (
                                  <div style={{ marginBottom: '20px' }}>
                                    <strong style={{ display: 'block', fontSize: '14px', color: '#667eea', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                      📋 How You Performed
                                    </strong>
                                    <p style={{ margin: 0, fontSize: '15px', color: '#2d3748', lineHeight: 1.7, padding: '12px', background: '#f7fafc', borderRadius: '8px', borderLeft: '3px solid #667eea' }}>
                                      {selectedResponse.overallFeedback.studentSummary}
                                    </p>
                                  </div>
                                )}

                                {selectedResponse.overallFeedback.studentStrengths && selectedResponse.overallFeedback.studentStrengths.length > 0 && (
                                  <div style={{ marginBottom: '20px' }}>
                                    <strong style={{ display: 'block', fontSize: '14px', color: '#16a34a', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                      ✓ Your Strengths
                                    </strong>
                                    <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '15px', color: '#15803d', background: '#f0fdf4', padding: '16px 16px 16px 40px', borderRadius: '8px', border: '2px solid #86efac' }}>
                                      {selectedResponse.overallFeedback.studentStrengths.map((strength, idx) => (
                                        <li key={idx} style={{ marginBottom: '6px', lineHeight: 1.6 }}>{strength}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {selectedResponse.overallFeedback.studentImprovements && selectedResponse.overallFeedback.studentImprovements.length > 0 && (
                                  <div style={{ marginBottom: '20px' }}>
                                    <strong style={{ display: 'block', fontSize: '14px', color: '#d97706', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                      💡 Areas to Focus On
                                    </strong>
                                    <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '15px', color: '#92400e', background: '#fffbeb', padding: '16px 16px 16px 40px', borderRadius: '8px', border: '2px solid #fcd34d' }}>
                                      {selectedResponse.overallFeedback.studentImprovements.map((improvement, idx) => (
                                        <li key={idx} style={{ marginBottom: '6px', lineHeight: 1.6 }}>{improvement}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {selectedResponse.overallFeedback.missedTerminology && selectedResponse.overallFeedback.missedTerminology.length > 0 && (
                                  <div style={{ marginBottom: '20px' }}>
                                    <strong style={{ display: 'block', fontSize: '14px', color: '#dc2626', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                      🎓 Important Terms to Learn
                                    </strong>
                                    <div style={{ background: '#fef2f2', padding: '16px', borderRadius: '8px', border: '2px solid #fca5a5' }}>
                                      <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#991b1b', fontStyle: 'italic' }}>
                                        These terms would have strengthened your answers:
                                      </p>
                                      <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '15px', color: '#991b1b' }}>
                                        {selectedResponse.overallFeedback.missedTerminology.map((term, idx) => (
                                          <li key={idx} style={{ marginBottom: '6px', lineHeight: 1.6 }}><strong>{term}</strong></li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                )}

                                {selectedResponse.overallFeedback.studentFeedforward && (
                                  <div style={{ marginBottom: '16px' }}>
                                    <strong style={{ display: 'block', fontSize: '14px', color: '#7c3aed', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                      🎯 Your Next Steps
                                    </strong>
                                    <div style={{ 
                                      padding: '16px', 
                                      background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', 
                                      borderRadius: '8px', 
                                      border: '2px solid #c084fc',
                                      fontSize: '15px',
                                      color: '#5b21b6',
                                      lineHeight: 1.7
                                    }}>
                                      {selectedResponse.overallFeedback.studentFeedforward}
                                    </div>
                                  </div>
                                )}
                          </div>
                        </div>
                      )}

                      <div style={{ padding: '20px', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <h4 style={{ color: '#667eea', fontSize: '16px', marginBottom: '16px' }}>Evaluation</h4>
                        <div className="form-group">
                          <label>Rating (0-10)</label>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            value={evaluation.rating}
                            onChange={(e) => setEvaluation(prev => ({ ...prev, rating: e.target.value }))}
                            placeholder="Enter rating..."
                          />
                        </div>
                        <div className="form-group">
                          <label>Notes/Feedback</label>
                          <textarea
                            value={evaluation.notes}
                            onChange={(e) => setEvaluation(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Add your feedback here..."
                            rows="4"
                          />
                        </div>
                        <button onClick={handleSaveEvaluation} className="btn btn-primary" style={{ width: '100%' }}>
                          Save Evaluation
                        </button>
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#718096' }}>
                      <p style={{ fontSize: '16px', margin: 0 }}>This student has not submitted the interview yet.</p>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#718096' }}>
                  <p style={{ fontSize: '16px', margin: 0 }}>Select a student to view their response</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(InterviewResponses);
